import React, {useEffect} from 'react';
import propTypes from 'prop-types';
import styled from '@emotion/styled';
import {Slider as MuiSlider, Typography, OutlinedInput} from '@mui/material';
import produce from "immer"

import toPrice from '../../../util/toPrice'
import {theme} from '../../../themes/default-theme';

const SliderRoot = styled.div`
  width: 100%;
`;

const SliderInput = styled(OutlinedInput)`
  max-width: 107px;
  width: 100%;
  display: inline-block;
  margin: 5px 0 10px;
  
  & input:not(:placeholder-shown) + fieldset {
    border-color: ${theme.palette.middleGrey};
  }
  
  & input {
    max-width: 61px;
  }
  
  &.input-padding input {
    padding-left: 12px;
    max-width: 61px;
  }
  
  &.small {
    max-width: 80px;
    
    & input {
      max-width: 44px;
    }
  }
  
  &.full-width {
    max-width: 100%;
    
    & input {
      max-width: calc(100% - 38px) !important;
    }
  }
`;

const SliderRow = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  align-items: center;
  z-index: 1;
`;

const ToText = styled.span`
  text-transform: none;
  padding: 0 5px;
  
  &.min-width {
    min-width: 50px;
    display: block;
    text-align: center;
  }
`;

const PricePrefix = styled.span`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: ${theme.palette.darkGrey};
  
  ${theme.breakpoints.up('md')} {
    font-size: 17px;
  }
`;

const SliderWrapper = styled.div`
  padding: 25px;
  margin: -25px;
  position: relative;
  overflow: hidden;
`;

const StyledSlider = styled(MuiSlider)`
  height: 12px;
  border-radius: 7px;
  
  & .MuiSlider-valueLabel {
    display: none;
  }
  
  & .MuiSlider-rail {
    height: 12px;
    border-radius: 7px;
    background-color: ${theme.palette.lightGrey};
    border: 1px solid ${theme.palette.middleGrey};
    opacity: 1;
    
    &:after {
      width: 7px;
      height: 12px;
      content: ${props => props.defaultVals?.length === 1 ? "''" : "none"};
      position: absolute;
      background-color: ${theme.palette.vividBlue};
      top: -1px;
      left: 1px;
      border-top-left-radius: 7px;
      border-bottom-left-radius: 7px;
      z-index: 2;
      border: 1px solid ${theme.palette.middleGrey};
      border-right: 0;
    }
    
    &:before {
      content: ${props => props.defaultVals?.length === 1 ? "''" : "none"};
      width: 50px;
      height: 14px;
      position: absolute;
      background-color: ${props => props.bg || 'white'};
      top: -1px;
      left: -43px;
      z-index: 1;
    }
  }
  
  & .MuiSlider-track {
    height: 12px;
    background-color: ${theme.palette.vividBlue};
    margin-top: 1px;
    border-radius: 7px;
    
    &:before {
      content: ${props => props.defaultVals?.length === 1 ? "''" : "none"};
      width: 20000px;
      height: 12px;
      display: block;
      left: -20000px;
      position: absolute;
      background-color: ${theme.palette.vividBlue};
    }
  }
  
  & .MuiSlider-thumb {
    width: 29px;
    height: 29px;
    // margin-top: -8px;
    // margin-left: -15px;
    background-color: white;
    border: 1px solid ${theme.palette.middleGrey};
    box-shadow: 0 0 10px 0 rgba(0,0,0,0.16);
    z-index: 3;
    
    &:before {
      width: 11px;
      height: 11px;
      content: '';
      display: block;
      background-color: ${theme.palette.charcoal};
      border-radius: 12px;
    }
    
    &:hover {
      box-shadow: 0 0 0 5px rgba(0,0,0,0.03);
    }
    
    &.Mui-active {
      box-shadow: 0 0 0 10px rgba(0,0,0,0.03) !important;
    }
  }
`;

const AfterLabel = styled.div`
  position: absolute;
  left: 150px;
  top: 50%;
  transform: translateY(-50%);
  
  &.small-input {
    left: 100px;
  }
`;


function valuetext(value) {
  return `$${value}`;
}

export default function UISlider(props) {

  const sliderProps = produce(props, draftProps => {
    delete draftProps['type'];
    delete draftProps['defaultVals'];
    delete draftProps['onSliderChange'];
    delete draftProps['afterLabel'];
    delete draftProps['inputSize'];
    delete draftProps['allowDecimals'];
    delete draftProps['bg'];
  })

  const [value, setValue] = React.useState(props.defaultVals);

  useEffect(() => {
    setValue(props.defaultVals)
  },[props.defaultVals])

  const handleChange = (event, newValue, update) => {
    const vals = newValue.map(item => {
      return props.allowDecimals ? parseFloat(item) : parseInt(item)
    })

    if (value.length > 1) {
      if (vals[0] >= vals[1]) {
        return
      }
    }

    props.onSliderChange(vals)

    update !== false && setValue(vals);
  };

  const keypressHandler = (e) => {
   if (e.key === "Enter") {
    e.target.blur();
    e.preventDefault();
   }
  }

  const handleInputChange = (e, index, blur) => {

    let val = props.allowDecimals ? e.target.value.replace(/,/g, '') : parseFloat(e.target.value.replace(/,/g, ''));

    val = (isNaN(val) || val < 0) ? 0 : val

    if (val < props.min && blur) {
      handleChange(null, produce(value, draftState => {
        if (value.length > 1) {
          draftState[index] = index === 0 ? props.min : value[0] + 1
        } else {
          draftState[0] = props.min
        }
      }))
      return
    }

    else if (val > props.max && blur) {
      handleChange(null, produce(value, draftState => {
        if (value.length > 1) {
          draftState[index] = index === 1 ? props.max : value[1] - 1
        } else {
          draftState[0] = props.max
        }
      }))
      return
    }

    else { 
      const newVal = produce(value, draftState => {
        if (value.length > 1) {
          if (!blur) {
            draftState[index] = val
          } else {
            if (index === 1 && val <= value[0]) {
              draftState[0] = value[1] - 1
            } 
            if (index === 0 && val >= value[1]) {
              draftState[1] = value[0] + 1
            }
          }
        } else {
          draftState[index] = val
        }
      })
      setValue(newVal);
      handleChange(null, newVal, false);
    }
  }

  const getInputClassName = (isPriceType, inputSize) => {
    let className = '';
    if (isPriceType) className += 'input-padding ';
    if (inputSize === 'small') className += 'small ';
    if (inputSize === 'fullWidth') className += 'full-width ';
    return className.trim();
  };

  return (
    <SliderRoot>
      <Typography gutterBottom>
        {props.title}
      </Typography>

      <SliderRow>
        {props.defaultVals.map((j, index) => (
          <React.Fragment key={index}>
            {index === 1 && props.defaultVals.length > 1 && 
              <Typography>
                <ToText className={props.inputSize === 'fullWidth' ? 'min-width' : ''}>
                  to
                </ToText>
              </Typography>
            }
            <SliderInput
              type="tel"
              className={getInputClassName(props.type === 'price', props.inputSize)}
              onChange={(e) => handleInputChange(e, index)}
              onBlur={(e) => handleInputChange(e, index, true)}
              onKeyPress={event => keypressHandler(event, index)}
              value={props.type === 'price' ? (toPrice(value[index]) || '') : value[index]}
              startAdornment={props.type === 'price' ? (
                <Typography>
                  <PricePrefix>$</PricePrefix>
                </Typography>
              ) : ''}
            />
            {props.afterLabel &&
              <AfterLabel className={props.inputSize === 'small' ? 'small-input' : ''}>
                <Typography>{props.afterLabel}</Typography>  
              </AfterLabel>
            }
          </React.Fragment>
        ))}
      </SliderRow>

      <SliderWrapper>
        <StyledSlider
          defaultVals={props.defaultVals}
          bg={props.bg}
          value={value}
          onChange={handleChange}
          valueLabelDisplay="on"
          aria-labelledby="range-slider"
          getAriaValueText={valuetext}
          {...sliderProps}
        />
      </SliderWrapper>
    </SliderRoot>
  );
}

UISlider.propTypes = {
  type: propTypes.oneOf(['price', 'regular']),
  defaultVals: propTypes.array,
  onSliderChange: propTypes.func,
  bg: propTypes.string,
  afterLabel: propTypes.string,
  inputSize: propTypes.oneOf(['small', 'medium']),
  allowDecimals: propTypes.bool
  // Other props here: https://material-ui.com/api/slider/
}

UISlider.defaultProps = {
  type: 'regular',
  defaultVals: [],
  onSliderChange: () => {},
  bg: 'white',
  afterLabel: null,
  inputSize: 'medium',
  allowDecimals: false,
  // Other props here: https://material-ui.com/api/slider/
}
